import asyncEachLimit from 'async/eachLimit';
import asyncRetry from 'async/retry';
import React from 'react';
import EventEmitter from 'events';

import util from '../tools/util';
import UserStore from './user_store';

export default {
  init,
  useMedia,
  fetch,
  del,
  deleteList,
  undelete,
  undeleteList,
  addMedia,
  getUrl,
  getVersionUrl,
  restoreMedia,
};

const g_grantMediaMap: any = new Map<string, any>();

const g_eventEmitter = new EventEmitter();
const CHANGE_EVENT = 'change';
function _emit(...args) {
  g_eventEmitter.emit(CHANGE_EVENT, ...args);
}
function _subscribe(callback: any) {
  g_eventEmitter.on(CHANGE_EVENT, callback);
  return () => {
    g_eventEmitter.removeListener(CHANGE_EVENT, callback);
  };
}
function init(done) {
  done?.();
}
function _getMedia(media_grant_id: number, media_id: number) {
  const key = `${media_grant_id}-${media_id}`;
  return g_grantMediaMap.get(key);
}
export function useMedia(media_grant_id: number, media_id: number) {
  const _get = React.useCallback(
    () => _getMedia(media_grant_id, media_id),
    [media_grant_id, media_id]
  );
  return React.useSyncExternalStore(_subscribe, _get);
}
function fetch(params: any, done?: any) {
  const { org_id, media_grant_id, media_id } = params;
  const opts = {
    url: `/web/1/org/${org_id}/media_grant/${media_grant_id}/media/${media_id}`,
  };
  UserStore.get(opts, (err, body) => {
    if (err) {
      util.errorLog('MediaStore.fetch: err:', err, body);
    } else {
      const key = `${media_grant_id}-${media_id}`;
      const old = g_grantMediaMap.get(key);
      if (!util.deepEqual(old, body)) {
        g_grantMediaMap.set(key, body);
        _emit('update_version');
      }
    }
    done?.(err, body);
  });
}
function del(params: any, done: (err: string | null) => void) {
  const { org_id, media_grant_id, media_id } = params;
  const opts = {
    url: `/web/1/org/${org_id}/media_grant/${media_grant_id}/media/${media_id}`,
  };
  UserStore.del(opts, (err, body) => {
    if (err) {
      util.errorLog('MediaStore.del: err:', err, body);
    }
    done(err);
  });
}
function deleteList(params: any, done: (err?: string | null) => void) {
  const { org_id, media_grant_id, media_list, on_progress } = params;

  const total = media_list.length;
  let count = 0;
  asyncEachLimit(
    media_list,
    5,
    (media, done) => {
      del({ org_id, media_grant_id, media_id: media.media_id }, (err) => {
        if (!err) {
          count++;
          on_progress({ count, total });
        }
        done(err);
      });
    },
    done
  );
}
function undelete(params: any, done: (err: string | null) => void) {
  const { org_id, media_grant_id, media_id } = params;
  const opts = {
    url: `/web/1/org/${org_id}/media_grant/${media_grant_id}/media/${media_id}/undelete`,
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('MediaStore.undelete: err:', err, body);
    }
    done(err);
  });
}
function undeleteList(params: any, done: (err?: string | null) => void) {
  const { org_id, media_grant_id, media_list, on_progress } = params;

  const total = media_list.length;
  let count = 0;
  asyncEachLimit(
    media_list,
    5,
    (media, done) => {
      undelete({ org_id, media_grant_id, media_id: media.media_id }, (err) => {
        if (!err) {
          count++;
          on_progress({ count, total });
        }
        done(err);
      });
    },
    done
  );
}
function addMedia(params, done) {
  const {
    org_id,
    media_grant_id,
    media_path,
    file_size,
    file_hash,
    ctime,
    mtime,
  } = params;
  const opts = {
    url: `/web/1/org/${org_id}/media_grant/${media_grant_id}/media`,
    body: {
      media_path,
      file_size,
      file_hash,
      ctime: new Date(ctime),
      mtime: new Date(mtime),
    },
    retry: true,
  };
  UserStore.post(opts, (err, body) => {
    let media;
    if (!err) {
      media = {
        media_id: body.media_id,
        media_path,
        file_size,
        file_hash,
        ctime,
        mtime,
      };
    }
    done(err, media);
  });
}
function getUrl(params, done) {
  const { org_id, media_grant_id, media_id, inline } = params;
  const opts = {
    url: `/web/1/org/${org_id}/media_grant/${media_grant_id}/media/${media_id}/download_url`,
    query: { inline: inline ? 1 : 0 },
  };
  UserStore.get(opts, (err, body) => {
    if (err) {
      util.errorLog('MediaStore.getUrl: err:', err, body);
    }
    done(err, body?.url);
  });
}
function getVersionUrl(params, done) {
  const { org_id, media_grant_id, media_version_id, inline } = params;
  const opts = {
    url: `/web/1/org/${org_id}/media_grant/${media_grant_id}/media_version/${media_version_id}/download_url`,
    query: { inline: inline ?? false },
  };
  UserStore.get(opts, (err, body) => {
    if (err) {
      util.errorLog('MediaStore.getVersionUrl: err:', err, body);
    }
    done(err, body?.url);
  });
}
function restoreMedia(params, done) {
  const { org_id, media_grant_id, file_hash, file_size, name, ext } = params;
  const create_time = params.create_time.replace(/:/g, '-');
  const dir = params.dir.length > 1 ? params.dir : '';
  const ctime = Date.now();
  let index = 0;
  let new_path;
  asyncRetry(
    { times: 5 },
    (done) => {
      const inc = index++ > 0 ? ` ${index}` : '';
      new_path = `${dir}/${name} (${create_time})${inc}${ext}`;
      const opts = {
        org_id,
        media_grant_id,
        media_path: new_path,
        file_size,
        file_hash,
        ctime,
        mtime: ctime,
      };
      addMedia(opts, (err, body) => {
        if (err) {
          util.errorLog('MediaStore.restoreMedia: err:', err, body);
        }
        done(err);
      });
    },
    (err) => done(err, new_path)
  );
}
